// Generic PDF fallback — best-effort parser for any text-native PDF
// statement that ISN'T BMO. Port of scripts/loaders.py::load_generic_pdf.
//
// Strategy:
//   1. Extract structured lines via PDF.js (already wraps the worker for us).
//   2. Walk lines looking for either:
//        single-line: `<date>  <description>  <amount>`
//        two-line:    `<date>` (alone) then `<description>  <amount>` (next)
//   3. Date patterns cover ISO, slash/dash, "Apr 15", "15 Apr 2026", etc.
//      Short dates like "Apr. 15" get year-filled from the statement period.
//   4. Sign: parse_amount handles CR/parens/leading-minus, so the parsed
//      sign passes through unchanged — PDFs print expenses as positive
//      numbers (charges) and refunds as " CR" or parenthesized.
//
// What this does NOT do (vs the Python original):
//   - Doesn't penalize lines where the rightmost numeric token is far from
//     the line end. The Python parser checks `last_amt.end() >= len(line)-3`
//     to skip rows where a balance column sits after the amount; we apply
//     the same check.
//   - Doesn't run the column-position-based debit/credit split (that's
//     bank-specific; the per-bank PDF parsers handle it).

import {
  type Transaction,
  type AccountKind,
  newTransaction,
  makeId,
  parseDate,
  parseAmount,
  normalizeVendor,
  isInterPortalTransfer,
} from "../common.js";
import { extractPdfLines } from "../pdf.js";

// Two date-pattern flavors — one for "date IS the whole line" (two-line
// layout) and one for "date prefixes the line" (single-line layout).
// Patterns intentionally permissive: ISO, slash/dash mixed, full month
// names ("April 15, 2026"), short month names ("Apr 15"), 8-digit dates.
const DATE_PATTERNS = [
  // YYYY-MM-DD / YYYY/MM/DD
  String.raw`\d{4}[-/]\d{1,2}[-/]\d{1,2}`,
  // MM/DD/YYYY / DD-MM-YYYY  (the parser tries multiple orders)
  String.raw`\d{1,2}[-/]\d{1,2}[-/]\d{2,4}`,
  // 15 April 2026
  String.raw`\d{1,2}\s+[A-Za-z]{3,9}\s+\d{2,4}`,
  // April 15, 2026 / Apr. 15, 2026
  String.raw`[A-Za-z]{3,9}\.?\s+\d{1,2},?\s+\d{2,4}`,
  // Short: "Apr 15" — needs statement-year fill
  String.raw`[A-Za-z]{3}\.?\s+\d{1,2}`,
  // Short: "04/29" — needs statement-year fill. Wells Fargo and similar US
  // bank PDFs emit MM/DD without a year. Comes LAST so that 04/29/2026
  // matches the YYYY-bearing variants first.
  String.raw`\d{1,2}[-/]\d{1,2}`,
];
// `(?:\d{1,4}\s+)?` lets the regex skip a leading row/ref number that
// some statements prepend before the date column (Yodlee, certain
// credit-card layouts: "001  Apr 8  Payment  -2,000.00"). 1-4 digits +
// whitespace is permissive enough to catch reference numbers without
// swallowing a real ISO date like "2026-04-01" (the "-" right after the
// year fails the `\s+` requirement).
const DATE_PREFIX_RE = new RegExp(
  `^\\s*(?:\\d{1,4}\\s+)?(${DATE_PATTERNS.join("|")})\\b\\s*(.*?)\\s*$`,
);
const DATE_ONLY_RE = new RegExp(
  `^\\s*(?:\\d{1,4}\\s+)?(${DATE_PATTERNS.join("|")})\\s*$`,
);

// Money pattern — same shape the Python AMOUNT_RE matches. Optional
// currency prefix, optional negative or parens, mandatory `.NN` decimal,
// optional " CR" suffix.
const AMOUNT_RE_GLOBAL = /(?:\$|€|£|¥|USD|CAD|EUR|GBP)?\s*\(?-?[\d,]+\.\d{2}\)?(?:\s*CR)?/gi;

// Reuse the personal/business non-tx + inter-portal filters by importing
// our existing helpers in the generic module.
import {
  _internal as genericInternal,
} from "./generic.js";
const { isNonTxRow, detectGenericInstitution, composeAccountLabel: composeAccountLabelFromInternal } = genericInternal;

// Statement-year discovery for filling in short dates like "Apr 15".
// Mirrors _find_generic_statement_year in scripts/loaders.py.
function findStatementYear(allText: string): number {
  const patterns = [
    /period\s+ending\s+\w+\.?\s+\d+,?\s+(\d{4})/i,
    /statement\s+date\s*:?\s*\w+\.?\s+\d+,?\s+(\d{4})/i,
    /statement\s+period\s+\w+\.?\s+\d+,?\s+(\d{4})/i,
    /(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\w*\.?\s+\d+,?\s+(\d{4})/i,
  ];
  for (const pat of patterns) {
    const m = allText.match(pat);
    if (m) {
      const y = parseInt(m[1], 10);
      if (y >= 2000 && y <= 2100) return y;
    }
  }
  return new Date().getFullYear();
}

// parse_date with a year-fill retry for short forms like "Apr 15".
// Mirrors _parse_short_or_full_date in scripts/loaders.py.
function parseShortOrFullDate(dateRaw: string, statementYear: number): string | null {
  try {
    return parseDate(dateRaw);
  } catch {
    // fall through
  }
  // Strip trailing period from a leading 3-letter month abbreviation
  // ("Apr." → "Apr") and retry.
  const normalized = dateRaw.replace(/^([A-Za-z]{3})\.\s+/, "$1 ");
  if (normalized !== dateRaw) {
    try {
      return parseDate(normalized);
    } catch {
      // continue
    }
  }
  for (const candidate of [dateRaw, normalized]) {
    for (const sep of [", ", " "]) {
      try {
        return parseDate(`${candidate}${sep}${statementYear}`);
      } catch {
        continue;
      }
    }
  }
  // Short MM/DD or DD/MM forms (Wells Fargo: "04/29") — append the
  // statement year with the SAME slash/dash separator the input used and
  // retry. We don't try DD/MM order here; the project-wide default in
  // parseDate handles US-first then falls back to DD/MM when day > 12.
  const shortSlash = /^(\d{1,2})([\-/])(\d{1,2})$/.exec(dateRaw.trim());
  if (shortSlash) {
    const [, a, sep, b] = shortSlash;
    try {
      return parseDate(`${a}${sep}${b}${sep}${statementYear}`);
    } catch {
      // fall through
    }
  }
  return null;
}

// Does a line END with an amount-shaped token (optional currency, digits.NN,
// optional ")"/" CR")? Used to validate two-line layouts.
function endsWithAmount(line: string): boolean {
  return /[\d,]+\.\d{2}\)?(?:\s*CR)?\s*$/i.test(line);
}

// Diagnostic helper: does this PDF have at least 3 lines that look like
// generic-PDF transaction rows? If yes, calling load is worth the work.
export async function hasGenericPdfLines(
  data: ArrayBuffer | Uint8Array,
): Promise<boolean> {
  const lines = await extractTextLines(data);
  let hits = 0;
  for (let i = 0; i < lines.length; i++) {
    const ln = lines[i];
    if (DATE_PREFIX_RE.test(ln) && endsWithAmount(ln)) {
      hits++;
      if (hits >= 3) return true;
      continue;
    }
    if (DATE_ONLY_RE.test(ln) && i + 1 < lines.length && endsWithAmount(lines[i + 1])) {
      hits++;
      if (hits >= 3) return true;
    }
  }
  return false;
}

// Reduce structured PDF.js items to plain text lines.
//
// **Critical:** Many PDFs are kerned at the glyph level — PDF.js extracts
// "$1,234.56" as separate items (`"$" "1" "," "234" "." "56"`) with
// small visual gaps between them. Joining naïvely with a single space
// destroys the amount regex on those documents (Yodlee, some CC
// statements). So we look at the visual gap between adjacent items and
// only insert a space when the gap is wide enough to look like word
// separation. The threshold (default 1.5 user units) is well below the
// typical word gap (5–10 units in 9–10 pt fonts) and well above kerned-
// glyph gaps (often 0.5–2 units).
const KERN_GAP_THRESHOLD = 1.5;
async function extractTextLines(
  data: ArrayBuffer | Uint8Array,
  opts: { kernThreshold?: number } = {},
): Promise<string[]> {
  const threshold = opts.kernThreshold ?? KERN_GAP_THRESHOLD;
  const pdfLines = await extractPdfLines(data);
  return pdfLines.map((l) => {
    let out = "";
    let prevEndX = -Infinity;
    for (const it of l.items) {
      if (out.length > 0) {
        const gap = it.x - prevEndX;
        if (gap > threshold) out += " ";
      }
      out += it.str;
      prevEndX = it.endX;
    }
    return out.replace(/\s+/g, " ").trim();
  }).filter((s) => s.length > 0);
}

// Section headers that flip the sign for subsequent transaction rows
// (Wells Fargo-style statements that segregate deposits and withdrawals
// into labelled sections with a single amount column each). A line is
// treated as a section header when it matches one of these patterns
// AND has no numeric amount on it. Headers stay in effect until the
// next matching header is seen (or until a "Total ..." line, which
// every section ends with — treated as section-close).
type SectionSign = "positive" | "negative" | null;
const SECTION_HEADERS: Array<{ pattern: RegExp; sign: SectionSign }> = [
  { pattern: /^\s*(deposits?|credits?|deposits?\s+and\s+(other\s+)?(credits?|interest))\s*$/i, sign: "negative" },
  { pattern: /^\s*(withdrawals?|debits?|withdrawals?\s+and\s+(other\s+)?(debits?|fees?)|checks?\s+paid|debit\s+(card\s+)?(?:purchases|transactions)|atm\s+withdrawals?)\s*$/i, sign: "positive" },
  { pattern: /^\s*(fees?|service\s+charges?|other\s+charges?)\s*$/i, sign: "positive" },
];

function detectSectionHeader(line: string, hasAmount: boolean): SectionSign | "close" {
  if (hasAmount) return null;
  if (/^\s*total\b/i.test(line)) return "close";
  for (const { pattern, sign } of SECTION_HEADERS) {
    if (pattern.test(line)) return sign;
  }
  return null;
}

// Apply a section sign to a parsed amount.
//   "negative" section (deposits/credits) — money in → income (negative)
//   "positive" section (withdrawals/debits) — money out → expense (positive)
// If the file ALSO ships explicit signs ("-50.00") the section pass is
// suppressed by sign() returning the raw amount when it's already
// negative-magnitude in a positive section, or vice versa. We never
// flip a sign that was explicit in the PDF.
function applySectionSign(amount: number, section: SectionSign): number {
  if (section === null) return amount;
  if (section === "negative") {
    // Income — must be negative. If the file printed "+250.00" or "250.00", flip.
    return amount > 0 ? -Math.abs(amount) : amount;
  }
  // section === "positive" — expense, must be positive.
  return amount < 0 ? amount : Math.abs(amount);
}

export async function loadGenericPdf(
  data: ArrayBuffer | Uint8Array,
  filename: string,
): Promise<Transaction[]> {
  const lines = await extractTextLines(data);
  if (lines.length === 0) return [];
  const allText = lines.join("\n");

  const statementYear = findStatementYear(allText);
  const { institution, kind: institutionKind } = detectGenericInstitution(allText, filename);
  const stemFallback =
    filename.replace(/\.[^.]+$/, "").toLowerCase().replace(/\s+/g, "_").slice(0, 40) || "generic";
  // Reuse the friendly-label composer from generic.ts so "BMO Chequing"
  // shows up on PDF imports too instead of bare "chequing" or the
  // gnarly filename stem.
  const account = composeAccountLabelFromInternal(institution, institutionKind, stemFallback);
  // Generic PDFs typically print credit/debit columns or signed amounts;
  // parse_amount handles CR / parens / leading-minus already, so no sign
  // flip happens here. account_kind is still useful as metadata for
  // downstream consumers (e.g. dashboard filters / sign-validation).
  const accountKind: AccountKind =
    institutionKind === "credit" ? "liability" :
    institutionKind === "chequing" || institutionKind === "savings" ? "debit" :
    "";

  const out: Transaction[] = [];
  const consumed = new Set<number>();
  let activeSection: SectionSign = null;

  for (let i = 0; i < lines.length; i++) {
    if (consumed.has(i)) continue;
    const line = lines[i];

    // Collect amount tokens with their positions in the line.
    const amounts: Array<{ start: number; end: number; text: string }> = [];
    AMOUNT_RE_GLOBAL.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = AMOUNT_RE_GLOBAL.exec(line)) !== null) {
      amounts.push({ start: m.index, end: m.index + m[0].length, text: m[0] });
    }

    // Update active section based on this line. Section headers don't
    // carry transactions, so we read them and move on.
    const sectionAction = detectSectionHeader(line, amounts.length > 0);
    if (sectionAction === "close") {
      activeSection = null;
      continue;
    }
    if (sectionAction !== null) {
      activeSection = sectionAction;
      continue;
    }

    const dateOnlyM = DATE_ONLY_RE.exec(line);
    const datePrefixM = dateOnlyM ? null : DATE_PREFIX_RE.exec(line);

    // Two-line layout: this line is a bare date, next line has the desc + amount.
    if (dateOnlyM && amounts.length === 0 && i + 1 < lines.length) {
      const nextLine = lines[i + 1];
      const nextAmounts: Array<{ start: number; end: number; text: string }> = [];
      AMOUNT_RE_GLOBAL.lastIndex = 0;
      while ((m = AMOUNT_RE_GLOBAL.exec(nextLine)) !== null) {
        nextAmounts.push({ start: m.index, end: m.index + m[0].length, text: m[0] });
      }
      // Require the trailing amount to be near the line end (within 3 chars)
      // so we don't pick up a "$5 stamp" mid-sentence.
      if (nextAmounts.length > 0 && nextAmounts[nextAmounts.length - 1].end >= nextLine.length - 3) {
        const dateRaw = dateOnlyM[1].trim();
        const dateStr = parseShortOrFullDate(dateRaw, statementYear);
        if (!dateStr) continue;
        const lastAmt = nextAmounts[nextAmounts.length - 1];
        let amount: number;
        let description: string;
        if (nextAmounts.length >= 2) {
          const penult = nextAmounts[nextAmounts.length - 2];
          const gap = nextLine.slice(penult.end, lastAmt.start).trim();
          if (gap.length <= 6) {
            // Tx amount + balance pair — use the penultimate.
            amount = parseAmount(penult.text.trim());
            description = nextLine.slice(0, penult.start).trim();
          } else {
            amount = parseAmount(lastAmt.text.trim());
            description = nextLine.slice(0, lastAmt.start).trim();
          }
        } else {
          amount = parseAmount(lastAmt.text.trim());
          description = nextLine.slice(0, lastAmt.start).trim();
        }
        description = description.replace(/\s{2,}/g, " ").trim();
        // Skip rows with no real description — see multi-line branch comment.
        if (!description || isNonTxRow(description) || isInterPortalTransfer(description)) {
          consumed.add(i + 1);
          continue;
        }
        amount = applySectionSign(amount, activeSection);
        const id = await makeId("generic", account, dateStr, amount, description);
        out.push(
          newTransaction({
            id,
            date: dateStr,
            vendor_raw: description,
            vendor_key: normalizeVendor(description) || "unknown",
            amount: Math.round(amount * 100) / 100,
            portal: "generic",
            account,
            account_kind: accountKind,
            institution_name: institution,
            source_file: filename,
            raw: { line, continuation: nextLine, statement_year: statementYear, section: activeSection },
          }),
        );
        consumed.add(i + 1);
        continue;
      }
    }

    // Multi-line layout (Wells Fargo style): date-prefixed line carrying
    // a partial description but NO amount. Look forward up to 3 lines
    // for a continuation that ends with an amount, merging any
    // intermediate description tokens. Stops at the next date line, a
    // section header, or after 3 lines without an amount.
    if (datePrefixM && amounts.length === 0) {
      const dateRaw = datePrefixM[1].trim();
      const partialDesc = datePrefixM[2].trim();
      let mergedDesc = partialDesc;
      let mergedAmount: number | null = null;
      let amountLineIdx = -1;
      for (let j = i + 1; j < Math.min(i + 4, lines.length); j++) {
        if (consumed.has(j)) break;
        const fwd = lines[j];
        if (DATE_PREFIX_RE.test(fwd) || DATE_ONLY_RE.test(fwd)) break;
        if (detectSectionHeader(fwd, false) !== null) break;
        const fwdAmounts: Array<{ start: number; end: number; text: string }> = [];
        AMOUNT_RE_GLOBAL.lastIndex = 0;
        let fm: RegExpExecArray | null;
        while ((fm = AMOUNT_RE_GLOBAL.exec(fwd)) !== null) {
          fwdAmounts.push({ start: fm.index, end: fm.index + fm[0].length, text: fm[0] });
        }
        if (fwdAmounts.length === 0) {
          // Pure description continuation — append and keep scanning.
          mergedDesc = (mergedDesc + " " + fwd).replace(/\s{2,}/g, " ").trim();
          continue;
        }
        const lastFwd = fwdAmounts[fwdAmounts.length - 1];
        // Amount must be near line end. If it's mid-line ("$5 stamp" type
        // noise), treat the line as description-only and keep scanning.
        if (lastFwd.end < fwd.length - 3) {
          mergedDesc = (mergedDesc + " " + fwd).replace(/\s{2,}/g, " ").trim();
          continue;
        }
        mergedAmount = parseAmount(lastFwd.text.trim());
        const tail = fwd.slice(0, lastFwd.start).trim();
        if (tail) mergedDesc = (mergedDesc + " " + tail).replace(/\s{2,}/g, " ").trim();
        amountLineIdx = j;
        break;
      }
      if (mergedAmount !== null && amountLineIdx >= 0) {
        const finalDesc = mergedDesc.trim();
        // Skip rows with no real description — these are daily-balance
        // ledger lines (Wells Fargo's "Daily ledger balance" section
        // emits date+balance with no transaction text) or other summary
        // noise. A real transaction always has SOME description.
        if (finalDesc && !isNonTxRow(finalDesc) && !isInterPortalTransfer(finalDesc)) {
          const dateStr = parseShortOrFullDate(dateRaw, statementYear);
          if (dateStr) {
            mergedAmount = applySectionSign(mergedAmount, activeSection);
            const id = await makeId("generic", account, dateStr, mergedAmount, finalDesc);
            out.push(
              newTransaction({
                id,
                date: dateStr,
                vendor_raw: finalDesc,
                vendor_key: normalizeVendor(finalDesc) || "unknown",
                amount: Math.round(mergedAmount * 100) / 100,
                portal: "generic",
                account,
                account_kind: accountKind,
                institution_name: institution,
                source_file: filename,
                raw: { line, statement_year: statementYear, section: activeSection, multi_line: true },
              }),
            );
            for (let k = i + 1; k <= amountLineIdx; k++) consumed.add(k);
            continue;
          }
        }
      }
    }

    // Single-line layout.
    if (!datePrefixM || amounts.length === 0) continue;
    const lastAmt = amounts[amounts.length - 1];
    if (lastAmt.end < line.length - 3) continue;
    const dateRaw = datePrefixM[1].trim();
    const afterDate = datePrefixM[2].trim();
    const dateStr = parseShortOrFullDate(dateRaw, statementYear);
    if (!dateStr) continue;

    let amount: number;
    let description: string;
    if (amounts.length >= 2) {
      const penult = amounts[amounts.length - 2];
      const gap = line.slice(penult.end, lastAmt.start).trim();
      if (gap.length <= 6) {
        amount = parseAmount(penult.text.trim());
        const preText = line.slice(0, penult.start).trim();
        const preMatch = DATE_PREFIX_RE.exec(preText);
        description = preMatch ? preMatch[2].trim() : afterDate;
      } else {
        amount = parseAmount(lastAmt.text.trim());
        const idx = afterDate.lastIndexOf(lastAmt.text);
        description = idx >= 0 ? afterDate.slice(0, idx).trim() || afterDate : afterDate;
      }
    } else {
      amount = parseAmount(lastAmt.text.trim());
      const idx = afterDate.lastIndexOf(lastAmt.text);
      description = idx >= 0 ? afterDate.slice(0, idx).trim() || afterDate : afterDate;
    }
    description = description.replace(/\s{2,}/g, " ").trim();
    // Skip empty-description rows — they're balance/summary lines, not txs.
    if (!description || isNonTxRow(description) || isInterPortalTransfer(description)) continue;

    amount = applySectionSign(amount, activeSection);
    const id = await makeId("generic", account, dateStr, amount, description);
    out.push(
      newTransaction({
        id,
        date: dateStr,
        vendor_raw: description,
        vendor_key: normalizeVendor(description) || "unknown",
        amount: Math.round(amount * 100) / 100,
        portal: "generic",
        account,
        account_kind: accountKind,
        institution_name: institution,
        source_file: filename,
        raw: { line, statement_year: statementYear, section: activeSection },
      }),
    );
  }
  return out;
}
